import useUser from "components/common/useUser";
import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { BUDGET_PLANNER_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getBudgetPlannerTranslations } from "components/website/marketing/i18n/pages/budgetPlanner";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/BudgetPlanner.scss";
import "../css/Website.scss";
import HeroVisual from "../img/screenshots/budgetplanner/Budget1.png";
import ExpenseTrackerVisual from "../img/screenshots/budgetplanner/Budget2.png";
import GroupSettlementVisual from "../img/screenshots/budgetplanner/Budget3.png";
import SpendingChartsVisual from "../img/screenshots/budgetplanner/Budget4.png";
import MultiCurrencyVisual from "../img/screenshots/budgetplanner/Budget5.png";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface BudgetPlannerPageProps {
	lang?: string;
}

export default function BudgetPlannerPage({ lang }: BudgetPlannerPageProps) {
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	const translations = getBudgetPlannerTranslations(lang || "en");
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div className="budget-planner" id="stippl-budget-planner">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="hero-s-mark-left" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "budgetPlanner.hero.chip", "Free travel budget planner")}</div>
						<h1>
							{t(translations, "budgetPlanner.hero.titlePrefix", "Free ")}
							<span className="hero__accent">{t(translations, "budgetPlanner.hero.titleAccent", "travel budget planner.")}</span>
							<br />
							{t(translations, "budgetPlanner.hero.titleSuffix", "Know exactly where your money goes.")}
						</h1>
						<p className="hr-hero__desc">
							{t(translations, "budgetPlanner.hero.descPre", "Stippl is a free")}{" "}
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)} style={{ color: "var(--main3)", textDecoration: "underline" }}>
								{t(translations, "budgetPlanner.hero.descLinkText", "trip planner")}
							</a>
							{t(translations, "budgetPlanner.hero.descPost", ", with a built-in expense tracker so you always know where your holiday money went.")}
						</p>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<div className="hero-glass-btn__icon">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</div>
								<span className="hero-glass-btn__label">{t(translations, "budgetPlanner.cta.downloadApp", "Download the app")}</span>
							</a>
							<a
								href="/register"
								className="hero-web-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "budgetPlanner.cta.startOnWeb", "Start on web →")}
							</a>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={HeroVisual}
						alt={t(translations, "budgetPlanner.hero.alt", "Budget planner screenshot")}
						loading="eager"
					/>
				</div>
			</section>

			{/* FEATURES GRID */}
			<section>
				<div className="section-wrapper" style={{ textAlign: "center" }}>
					<div className="section-chip chip-amber" style={{ margin: "0 auto 16px" }}>
						{t(translations, "budgetPlanner.features.chip", "Everything in one place")}
					</div>
					<h2>
						{t(translations, "budgetPlanner.features.titlePrefix", "Track your travel expenses,")}
						<br />
						<span>{t(translations, "budgetPlanner.features.titleAccent", "built into your itinerary")}</span>
					</h2>
					<p className="body-text" style={{ maxWidth: "560px", margin: "0 auto" }}>
						{t(
							translations,
							"budgetPlanner.features.description",
							"See exactly where every penny went: broken down by category, by day, by person. Accommodation, food, transport, activities: all connected to your itinerary, all shareable with whoever you're travelling with."
						)}
					</p>
					<div className="features-small-grid">
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main3light)" }}>
								<Icon name="database" size={20} stroke="var(--main3)" />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card1.title", "Track every expense")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"budgetPlanner.features.card1.text",
									"Log spending by day and destination, split into categories: stays, food, transport and activities."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main1light)" }}>
								<Icon name="peoples" size={20} pathProps={{ stroke: "var(--main1)" }} />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card2.title", "Split costs instantly")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"budgetPlanner.features.card2.text",
									"Add shared expenses, mark who paid and Stippl works out the simplest way to settle up."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main4light)" }}>
								<Icon name="globe" size={20} fill="var(--main4)" />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card3.title", "100+ currencies")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"budgetPlanner.features.card3.text",
									"Log in local currency at each stop. Stippl converts everything back to your home currency."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main2light)" }}>
								<Icon name="chart" size={20} stroke="var(--main2)" />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card4.title", "Charts & insights")}</div>
							<div className="feature-small-text">
								{t(translations, "budgetPlanner.features.card4.text", "See exactly where your money goes with visual breakdowns by category and destination.")}
							</div>
						</div>
						<div className="feature-small-card" style={{ position: "relative" }}>
							<span className="pro-badge pro-badge--card">PRO</span>
							<div className="feature-small-icon" style={{ background: "var(--main1light)" }}>
								<Icon name="export" size={20} fill="var(--main1)" />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card5.title", "Export to CSV")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"budgetPlanner.features.card5.text",
									"Download your full expense list as a spreadsheet, handy for reimbursements or keeping records."
								)}
							</div>
						</div>
						<div className="feature-small-card" style={{ position: "relative" }}>
							<span className="pro-badge pro-badge--card">PRO</span>
							<div className="feature-small-icon" style={{ background: "var(--main3light)" }}>
								<Icon name="tag" size={20} fill="var(--main3)" />
							</div>
							<div className="feature-small-title">{t(translations, "budgetPlanner.features.card6.title", "Custom categories")}</div>
							<div className="feature-small-text">
								{t(translations, "budgetPlanner.features.card6.text", "Add your own expense categories to match the way you actually travel.")}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: TRACK EXPENSES */}
			<section className="section-alt">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-amber">{t(translations, "budgetPlanner.trackExpenses.chip", "Travel expense tracker")}</div>
							<h2>
								{t(translations, "budgetPlanner.trackExpenses.titlePrefix", "Log every cost as you go.")}
								<br />
								<span>{t(translations, "budgetPlanner.trackExpenses.titleAccent", "Always know what you spent.")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"budgetPlanner.trackExpenses.description",
									"Stippl's travel expense tracker lets you add every cost in seconds: right from your phone at the restaurant, hotel or train station. No spreadsheet, no receipts pile. Just a clear, organised overview of your holiday spending, broken down by category and day."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="tag" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.trackExpenses.item1.title", "Organised by category")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.trackExpenses.item1.text",
												"Accommodation, food, transport, activities, every travel expense logged in the right place."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="calendar" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.trackExpenses.item2.title", "Linked to your days")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.trackExpenses.item2.text",
												"Each expense attaches to a specific day in your trip itinerary, see the full picture per day and destination."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="chart" size={18} stroke="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.trackExpenses.item3.title", "Full spending overview")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.trackExpenses.item3.text",
												"See your total trip costs at a glance: broken down by category, by day, and by person."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "budgetPlanner.trackExpenses.cta", "Start tracking your travel expenses →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={ExpenseTrackerVisual}
								alt={t(translations, "budgetPlanner.trackExpenses.alt", "Travel expense tracker screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: SPLIT COSTS */}
			<section>
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-green">{t(translations, "budgetPlanner.splitCosts.chip", "Group travel expense splitter")}</div>
							<h2>
								{t(translations, "budgetPlanner.splitCosts.titlePrefix", "Split travel costs.")}
								<br />
								<span>{t(translations, "budgetPlanner.splitCosts.titleAccent", "Everyone stays happy.")}</span>
							</h2>
							<p className="body-text">
								{t(translations, "budgetPlanner.splitCosts.descPre", "Stippl includes a free ")}
								<span>{t(translations, "budgetPlanner.splitCosts.descEmphasis", "group travel")}</span>
								{t(
									translations,
									"budgetPlanner.splitCosts.descPost",
									" expense splitter built into your trip. Log shared costs, mark who paid, and Stippl calculates who owes what, using the fewest transfers possible to settle everything up. No awkward conversations, no spreadsheets."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="scales" size={18} fill="var(--main1)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.splitCosts.item1.title", "Split evenly or unevenly")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.splitCosts.item1.text",
												"Divide shared travel costs equally or assign custom amounts per person: your trip, your rules."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="sparkles" size={18} fill="var(--main1)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.splitCosts.item2.title", "Automatic settlement")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.splitCosts.item2.text",
												"Stippl works out the simplest way to settle group expenses: fewest transfers, least confusion."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="peoples" size={18} pathProps={{ stroke: "var(--main1)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.splitCosts.item3.title", "Works for any group size")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.splitCosts.item3.text",
												"Two people or ten: split travel expenses across any size group trip, automatically."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "budgetPlanner.splitCosts.cta", "Split your group travel expenses →")}
							</a>
						</div>
						<div className="feature-visual feature-visual--white">
							<img
								src={GroupSettlementVisual}
								alt={t(translations, "budgetPlanner.splitCosts.alt", "Group travel expense splitter screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: INSIGHTS */}
			<section className="section-alt">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "budgetPlanner.insights.chip", "Budget insights")}</div>
							<h2>
								{t(translations, "budgetPlanner.insights.titlePrefix", "See where your money")}
								<br />
								<span>{t(translations, "budgetPlanner.insights.titleAccent", "actually goes.")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"budgetPlanner.insights.description",
									"After a few days on the road you can spot the patterns. Stippl's budget charts show your spending broken down by category and destination: so you can adjust before you run out, not after."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="chart" size={18} stroke="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.insights.item1.title", "Visual spending breakdown")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.insights.item1.text",
												"Charts by category, destination and day, see what's eating your budget at a glance."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="marker--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.insights.item2.title", "Cost per destination")}</h4>
										<p>{t(translations, "budgetPlanner.insights.item2.text", "Compare how much you spent in each city, useful for planning future trips.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="lightning" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.insights.item3.title", "Daily average tracker")}</h4>
										<p>
											{t(translations, "budgetPlanner.insights.item3.text", "Know your daily spend rate and whether you're on track to stay within budget.")}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "budgetPlanner.insights.cta", "See your spending insights →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={SpendingChartsVisual}
								alt={t(translations, "budgetPlanner.insights.alt", "Budget spending charts screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: MULTI-CURRENCY + EXPORT */}
			<section>
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-amber">{t(translations, "budgetPlanner.multiCurrency.chip", "Multi-currency & export")}</div>
							<h2>
								{t(translations, "budgetPlanner.multiCurrency.titlePrefix", "Log in any currency.")}
								<br />
								<span>{t(translations, "budgetPlanner.multiCurrency.titleAccent", "Total in yours.")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"budgetPlanner.multiCurrency.description",
									"Multi-country trip? No problem. Log expenses in the local currency at each destination and Stippl converts everything back automatically: so your total trip cost is always in one currency, not a dozen."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="change" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.multiCurrency.item1.title", "100+ currencies, automatic conversion")}</h4>
										<p>{t(translations, "budgetPlanner.multiCurrency.item1.text", "Pay in yen, baht, lira, Stippl handles the conversion in real time.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="export" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>
											{t(translations, "budgetPlanner.multiCurrency.item2.title", "Export to CSV")} <span className="pro-badge">PRO</span>
										</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.multiCurrency.item2.text",
												"Download your full trip expense data, useful for employer reimbursements or personal finance tracking."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="phone" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "budgetPlanner.multiCurrency.item3.title", "Viewable offline")}</h4>
										<p>
											{t(
												translations,
												"budgetPlanner.multiCurrency.item3.text",
												"Your logged expenses stay visible without a connection. Adding a new expense or switching currency needs you back online."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "budgetPlanner.multiCurrency.cta", "Start budgeting your trip →")}
							</a>
							<p style={{ fontFamily: "'Rubik',sans-serif", fontSize: "13px", color: "var(--darkgrey)", marginTop: "12px" }}>
								{t(translations, "budgetPlanner.multiCurrency.noteLinkPre", "Travelling internationally? A ")}
								<a href={localizeHref("/esim", lang as MarketingLangCode)} style={{ color: "var(--main3contrast)", fontWeight: 500 }}>
									{t(translations, "budgetPlanner.multiCurrency.noteLinkText", "travel eSIM")}
								</a>
								{t(
									translations,
									"budgetPlanner.multiCurrency.noteLinkPost",
									" cuts roaming costs dramatically, and you can track the saving right here in your budget."
								)}
							</p>
						</div>
						<div className="feature-visual">
							<img
								src={MultiCurrencyVisual}
								alt={t(translations, "budgetPlanner.multiCurrency.alt", "Multi-currency expense tracker screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="amber"
				tinted
				title={t(translations, "budgetPlanner.howToUse.name", BUDGET_PLANNER_CONTENT.howToUse.name)}
				description={t(translations, "budgetPlanner.howToUse.description", BUDGET_PLANNER_CONTENT.howToUse.description)}
				steps={BUDGET_PLANNER_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `budgetPlanner.howToSteps.${index}.name`, step.name),
					text: t(translations, `budgetPlanner.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "budgetPlanner.howItWorks.cta", "Start budgeting for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-left">
							<div className="pro-callout-label">{t(translations, "budgetPlanner.proCallout.label", "Stippl Pro")}</div>
							<h3>{t(translations, "budgetPlanner.proCallout.title", "More control over your travel budget")}</h3>
							<p>
								{t(
									translations,
									"budgetPlanner.proCallout.description",
									"The budget tracker is free. Pro adds the power-user features for travellers who want full control over their spending."
								)}
							</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "budgetPlanner.proCallout.feature1Strong", "Expense export to CSV")}</strong>
										{t(
											translations,
											"budgetPlanner.proCallout.feature1Text",
											": download your full trip spending for reimbursement, visa applications or just your own records"
										)}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "budgetPlanner.proCallout.feature2Strong", "Custom budget categories")}</strong>
										{t(
											translations,
											"budgetPlanner.proCallout.feature2Text",
											", go beyond the defaults and create categories that match how you actually travel"
										)}
									</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<svg className="pro-price-card-smark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d={S_PATH} fill="white" />
							</svg>
							<div className="pro-price-label">{t(translations, "budgetPlanner.proCallout.priceLabel", "Starting from")}</div>
							<div className="pro-price-amount">€3</div>
							<div className="pro-price-period">{t(translations, "budgetPlanner.proCallout.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "budgetPlanner.proCallout.priceBtn", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "budgetPlanner.proCallout.priceNote", "Cancel anytime · No commitment")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section>
				<div className="section-wrapper">
					<FAQSection
						heading={t(translations, "budgetPlanner.faq.heading", "Frequently asked questions")}
						items={BUDGET_PLANNER_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `budgetPlanner.faqItems.${index}.question`, item.question),
							answer: t(translations, `budgetPlanner.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA STRIP */}
			<section className="cta-strip">
				<svg className="cta-s-mark" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div style={{ position: "relative", zIndex: 2 }}>
					<h2 style={{ color: "#fff" }}>
						{t(translations, "budgetPlanner.ctaStrip.titlePrefix", "Know exactly where your")}
						<br />
						{t(translations, "budgetPlanner.ctaStrip.titleSuffix", "travel money went.")}
					</h2>
					<p>{t(translations, "budgetPlanner.ctaStrip.description", "Free for 1.5 million+ travellers. No spreadsheets, no guessing.")}</p>
					<div className="cta-strip-btns">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="hero-glass-btn"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							<div className="hero-glass-btn__icon">
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="#fff" />
								</svg>
							</div>
							<span className="hero-glass-btn__label">{t(translations, "budgetPlanner.cta.downloadApp", "Download the app")}</span>
						</a>
						<a
							href="/register"
							className="hero-web-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "budgetPlanner.cta.startOnWeb", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
